import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { brand, neutral } from '../../theme';

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** A quieter second action, shown as a text button. */
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  /** Optional product illustration. Non-compact states use the shared media illustration by default. */
  illustrationSrc?: string;
  /** Compact variant for panels/sidebars. */
  compact?: boolean;
}

/**
 * Shared empty state: a quiet icon or illustration, a headline, one line of
 * guidance and the next step, so blank screens point people somewhere instead
 * of dead-ending.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  illustrationSrc,
  compact = false
}) => {
  const hasPrimary = Boolean(actionLabel && onAction);
  const hasSecondary = Boolean(secondaryActionLabel && onSecondaryAction);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: compact ? 4 : 8,
        px: 3
      }}
    >
      {!compact ? (
        <Box
          component="img"
          src={illustrationSrc || '/brand/pithplay-empty-state.webp'}
          alt=""
          aria-hidden="true"
          sx={{ width: 'min(220px, 72vw)', height: 'auto', display: 'block', mb: 2.5 }}
        />
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: neutral[100],
            color: brand.slate,
            border: `1px solid ${brand.border}`,
            mb: 2,
            '& svg': { fontSize: 24 }
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        variant="h6"
        component="p"
        sx={{ fontWeight: 700, fontSize: compact ? '1rem' : '1.0625rem', color: 'text.primary', mb: 0.5 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mb: hasPrimary || hasSecondary ? 2.5 : 0 }}>
          {description}
        </Typography>
      )}
      {(hasPrimary || hasSecondary) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {hasPrimary && (
            <Button variant="contained" onClick={onAction}>
              {actionLabel}
            </Button>
          )}
          {hasSecondary && (
            <Button variant="text" onClick={onSecondaryAction}>
              {secondaryActionLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
